import { Alert, Box, Collapse, Divider, FormControlLabel, Grid, MenuItem, Paper, Switch } from "@mui/material";
import { VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { RangeStates } from "../../../../shared/utils";
import { AutoCompleteClientFire } from "../../components/AutoCompleteClientFire";
import { TokioMarineLogo } from "../../icons/TokioMarineLogo";
import { useFireVigency } from "./hooks/useFireVigency";
import { SectionHeaderQuote } from "./SectionHeaderQuote";
import { CoverageCardQuote } from "./CoverageCardQuote";

interface QuoteFormProps {
	formRef?: any;
	watch?: any;

	coverages: any[];
	onCoveragesChange: (c: any[]) => void;

	onClientSelected?: (id: string | null) => void;
	clientNameDefault?: string | null;
	propertys: any[];
	occupations: any[];
	assistances: any[];
	rentValue: string;

	onPropertyTypeChange?: (value: string) => void;
	onAssistaceChange?: (value: string) => void;
	onOccupationChange?: (value: string) => void;
	onRentValueChange?: (value: string) => void;
	onProlaboreChange?: (value: string) => void;
	onInstallmentChange?: (value: number) => void;
	onChangeCep?: (cep: string) => void;
	onTenantDocumentLookup?: (cpf: string) => void;
	onLessorDocumentLookup?: (cpfCnpj: string) => void;

	coverageType?: string;
	predialPercent?: string;
	contentPercent?: string;
	showOutras?: boolean;
	onCoverageTypeChange?: (type: string) => void;
	onPredialBlur?: (e: any) => void;
}

export const QuoteForm = ({
	formRef,
	watch,

	onClientSelected,
	clientNameDefault = "",

	propertys,
	occupations,
	assistances,

	onPropertyTypeChange,
	onAssistaceChange,
	onOccupationChange,
	onChangeCep,

	coverages,
	onCoveragesChange,

	rentValue,
	onRentValueChange,
	onProlaboreChange,
	onInstallmentChange,

	coverageType,
	predialPercent,
	contentPercent,
	showOutras,
	onCoverageTypeChange,
	onPredialBlur,
	onTenantDocumentLookup,
	onLessorDocumentLookup,
}: QuoteFormProps) => {
	const hasSocialName = watch?.("tenantHasSocialName");

	const {
		showAlert,
		vigencyStart,
		vigencyMonths,
		vigencyEnd,
		minDateObj,
		maxDateObj,
		minDateFormatted,
		maxDateFormatted,
		handleVigencyStartChange,
		handleVigencyMonthsChange,


	} = useFireVigency(formRef);

	return (
		<Paper sx={{ p: 2 }} variant="outlined">
			{/* Estipulante e Seguradora */}
			<Grid container spacing={2} mt={0.5}>
				<Grid item xs={12} sm={6}>
					<AutoCompleteClientFire
						namecampo="clientId"
						labelSelect="Estipulante (Imobiliária)"
						onChange={onClientSelected}
						nameDefault={clientNameDefault ?? ""}
						disabled={!!clientNameDefault}
					/>
				</Grid>

				<Grid item xs={12} sm={6}>
					<VTextField
						fullWidth
						label="Seguradora"
						name="insurance_companiesId"
						select
						size="medium"
						defaultValue="TOKIO_ID"
						disabled={!!clientNameDefault}
						SelectProps={{
							renderValue: () => (
								<Box sx={{ display: "flex", alignItems: "center" }}>
									<TokioMarineLogo height={40} />
								</Box>
							),
						}}
					>
						<MenuItem key="TOKIO_ID" value="TOKIO_ID">
							<Grid container alignItems="center" spacing={1} wrap="nowrap">
								<Grid item>
									<TokioMarineLogo height={52} />
								</Grid>
							</Grid>
						</MenuItem>
					</VTextField>
				</Grid>
			</Grid>

			<Divider sx={{ my: 3 }} />

			<SectionHeaderQuote title="Detalhes do Seguro" />

			{/* Tipo / Ocupação / Assistência */}
			<Grid container spacing={2} mt={0.5}>
				<Grid item xs={12} sm={6} md={4}>
					<VTextField fullWidth label="Tipo de Imóvel" name="propertyType" select onChange={(e) => onPropertyTypeChange?.(e.target.value)}>
						{propertys.map(({ value, label }) => (
							<MenuItem key={value} value={value}>
								{label}
							</MenuItem>
						))}
					</VTextField>
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VTextField fullWidth label="Ocupação" name="occupationId" select onChange={(e) => onOccupationChange?.(e.target.value)}>
						{occupations.map(({ value, label }) => (
							<MenuItem key={value} value={value}>
								{label}
							</MenuItem>
						))}
					</VTextField>
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VTextField fullWidth label="Assistência" name="assistanceId" select onChange={(e) => onAssistaceChange?.(e.target.value)}>
						{assistances.map(({ value, label }) => (
							<MenuItem key={value} value={value}>
								{label}
							</MenuItem>
						))}
					</VTextField>
				</Grid>
			</Grid>

			{/* Valores */}
			<Grid container spacing={2} mt={1}>
				<Grid item xs={12} sm={6} md={4}>
					<VMaskTextField
						fullWidth
						label="Valor do Aluguel"
						name="rent_value"
						mask="real"
						value={rentValue}
						onBlur={(e) => onRentValueChange?.(e.target.value)}
					/>
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VTextField
						fullWidth
						label="Parcelas"
						name="installment_number"
						select
						onChange={(e) => onInstallmentChange?.(Number(e.target.value))}
					>
						{Array.from({ length: 11 }, (_, i) => (
							<MenuItem key={i + 1} value={i + 1}>
								{i + 1}
							</MenuItem>
						))}
					</VTextField>
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VMaskTextField
						fullWidth
						label="PL (%)"
						name="prolabore_percent_amount"
						mask="real"
						onBlur={(e) => onProlaboreChange?.(e.target.value)}
					/>
				</Grid>
			</Grid>

			{/* Tipo de Cobertura (Prédio/Conteúdo) */}
			<Grid container spacing={2} mt={1}>
				<Grid item xs={12} sm={6} md={4}>
					<VTextField
						fullWidth
						select
						size="small"
						label="Tipo de Cobertura"
						name="coverageType"
						value={coverageType || "Predio_e_Conteudo_80_20"}
						onChange={(e) => onCoverageTypeChange?.(e.target.value)}
					>
						<MenuItem value="Predio_e_Conteudo_90_10">Prédio (90%) e Conteúdo (10%)</MenuItem>
						<MenuItem value="Predio_e_Conteudo_80_20">Prédio (80%) e Conteúdo (20%)</MenuItem>
						<MenuItem value="Predio_e_Conteudo_70_30">Prédio (70%) e Conteúdo (30%)</MenuItem>
						<MenuItem value="Predio_e_Conteudo_Outras">Prédio e Conteúdo (Outras)</MenuItem>
					</VTextField>
				</Grid>

				{showOutras && (
					<>
						<Grid item xs={12} sm={6} md={4}>
							<VMaskTextField
								fullWidth
								label="Prédio (%)"
								name="PredialPercent"
								mask="real"
								value={predialPercent}
								onBlur={onPredialBlur}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<VMaskTextField
								fullWidth
								label="Conteúdo (%)"
								name="ContentPercent"
								mask="real"
								value={contentPercent}
								disabled={true}
							/>
						</Grid>
					</>
				)}
			</Grid>

			<Divider sx={{ my: 3 }} />

			{/* Coberturas */}
			<Box mt={2}>
				<CoverageCardQuote coverages={coverages} rentValue={rentValue} onChange={(updated) => onCoveragesChange(updated)} />
			</Box>

			<Divider sx={{ my: 3 }} />

			{/* Endereço */}
			<SectionHeaderQuote title="Endereço" />

			<Grid container spacing={2}>
				<Grid item xs={12} sm={4}>
					<VMaskTextField fullWidth label="CEP" name="address_zipcode" mask="cep" onBlur={(e) => onChangeCep?.(e.target.value)} />
				</Grid>

				<Grid item xs={12} sm={4}>
					<VTextField fullWidth label="Rua" name="address_street" />
				</Grid>

				<Grid item xs={12} sm={4}>
					<VTextField fullWidth label="Número" name="address_number" />
				</Grid>

				<Grid item xs={12} sm={4}>
					<VTextField fullWidth label="Complemento" name="address_complement" />
				</Grid>

				<Grid item xs={12} sm={4}>
					<VTextField fullWidth label="Bairro" name="address_district" />
				</Grid>

				<Grid item xs={12} sm={4}>
					<VTextField fullWidth label="Cidade" name="address_city" />
				</Grid>

				<Grid item xs={12} sm={4}>
					<VTextField fullWidth label="Estado" name="address_state" select>
						{RangeStates.map(({ value, label }) => (
							<MenuItem key={value} value={value}>
								{label}
							</MenuItem>
						))}
					</VTextField>
				</Grid>
			</Grid>

			<Divider sx={{ my: 3 }} />

			{/* Vigência */}
			<SectionHeaderQuote title="Vigência" />

			<Collapse in={showAlert}>
				<Alert severity="info" sx={{ mb: 2 }}>
					A data de início foi ajustada automaticamente para o intervalo permitido:
					<b> {minDateFormatted}</b> até <b>{maxDateFormatted}</b>.
				</Alert>
			</Collapse>

			<Grid container spacing={2}>
				<Grid item xs={12} sm={6} md={4}>
					<VTextField
						fullWidth
						label="Início de vigência"
						name="validity_start"
						type="date"
						value={vigencyStart}
						onChange={handleVigencyStartChange}
						InputLabelProps={{ shrink: true }}
						inputProps={{
							min: minDateObj.format("YYYY-MM-DD"),
							max: maxDateObj.format("YYYY-MM-DD"),
						}}
					/>
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VTextField
						fullWidth
						label="Vigência (meses)"
						name="monthly_validity"
						select
						value={vigencyMonths}
						onChange={(e) => handleVigencyMonthsChange(Number(e.target.value))}
					>
						{Array.from({ length: 12 }, (_, i) => (
							<MenuItem key={i + 1} value={i + 1}>
								{i + 1}
							</MenuItem>
						))}
					</VTextField>
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VTextField
						fullWidth
						label="Final da Vigência"
						name="validity_end"
						type="date"
						value={vigencyEnd}
						disabled
						InputLabelProps={{ shrink: true }}
					/>
				</Grid>
			</Grid>

			<Divider sx={{ my: 3 }} />

			{/* Inquilino */}
			<SectionHeaderQuote title="Inquilino" />

			<Grid container spacing={2}>
				<Grid item xs={12} sm={6} md={4}>
					<VMaskTextField fullWidth label="CPF" name="tenantDocument" mask="cpf" onBlur={(e) => onTenantDocumentLookup?.(e.target.value)} />
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VTextField fullWidth label="Nome" name="tenantName" />
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VTextField fullWidth label="Data de nascimento" name="tenantBirthDate" type="date" />
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VMaskTextField fullWidth label="Telefone" name="tenantMobilePhone" mask="cell" />
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VTextField fullWidth label="E-mail" name="tenantEmail" />
				</Grid>

				<Grid item xs={12} sm={6} md={4} display="flex" alignItems="center">
					<FormControlLabel
						control={
							<Switch
								color="primary"
								onChange={(e) => {
									const checked = e.target.checked;
									formRef?.current?.setFieldValue("tenantHasSocialName", checked);
									if (!checked) {
										formRef?.current?.setFieldValue("tenantSocialName", "");
									}
								}}
							/>
						}
						label="Possui nome social?"
					/>
				</Grid>

				{hasSocialName && (
					<Grid item xs={12} sm={6} md={4}>
						<VTextField fullWidth label="Nome social" name="tenantSocialName" />
					</Grid>
				)}
			</Grid>

			<Divider sx={{ my: 3 }} />

			{/* Proprietário */}
			<SectionHeaderQuote title="Proprietário" />

			<Grid container spacing={2}>
				<Grid item xs={12} sm={6} md={4}>
					<VMaskTextField fullWidth label="CPF/CNPJ" name="lessorDocument" mask="cpf" onBlur={(e) => onLessorDocumentLookup?.(e.target.value)} />
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VTextField fullWidth label="Nome/Razão Social" name="lessorName" />
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<VTextField fullWidth label="E-mail" name="lessorEmail" />
				</Grid>
			</Grid>
		</Paper>
	);
};
